import { useEffect, useRef, useState } from 'react';
export interface GuidedScrollStep {
  key: string;
}
export interface UseGuidedStepScrollOptions {
  auto_scroll_on_progress_change?: boolean;
  scroll_on_advance_click?: boolean;
  progress: number;
  steps: GuidedScrollStep[];
  completion_milestone?: string;
}

/** Auto-scroll waits ~600ms after mount so SWR/route changes do not yank the viewport; advance clicks still scroll. */
export function useGuidedStepScroll({
  auto_scroll_on_progress_change = true,
  scroll_on_advance_click = true,
  progress,
  steps,
  completion_milestone
}: UseGuidedStepScrollOptions): {
  scroll_after_advance_click: (step_index: number, milestone_before: string) => void;
  scroll_to_step_key: (step_key: string) => void;
} {
  const progress_ref = useRef(progress);
  progress_ref.current = progress;
  const milestone_ref = useRef(completion_milestone ?? '');
  milestone_ref.current = completion_milestone ?? '';
  const prev_auto_scroll_anchor_ref = useRef<{
    progress: number;
    key: string | undefined;
  } | null>(null);
  const [auto_scroll_armed, set_auto_scroll_armed] = useState(false);
  useEffect(() => {
    const timeout_id = window.setTimeout(() => set_auto_scroll_armed(true), 600);
    return () => window.clearTimeout(timeout_id);
  }, []);
  const scroll_target_step_key = steps[progress]?.key;
  useEffect(() => {
    if (!auto_scroll_on_progress_change) return;
    const curr = {
      progress,
      key: scroll_target_step_key
    };
    if (curr.progress <= 0 || !curr.key) {
      prev_auto_scroll_anchor_ref.current = curr;
      return;
    }
    if (!auto_scroll_armed) {
      prev_auto_scroll_anchor_ref.current = curr;
      return;
    }
    const prev = prev_auto_scroll_anchor_ref.current;
    prev_auto_scroll_anchor_ref.current = curr;
    if (prev === null) return;
    if (prev.progress === curr.progress && prev.key === curr.key) return;
    const element = document.getElementById(curr.key);
    if (!element) return;
    element.scrollIntoView({
      behavior: 'smooth'
    });
  }, [auto_scroll_armed, auto_scroll_on_progress_change, progress, scroll_target_step_key]);
  const scroll_to_step_key = (step_key: string) => {
    const element_0 = document.getElementById(step_key);
    element_0?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scroll_after_advance_click = (step_index: number, milestone_before: string) => {
    if (!scroll_on_advance_click) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const milestone_after = milestone_ref.current;
        const completion_tracked = completion_milestone !== undefined;
        const advanced = completion_tracked && milestone_after !== milestone_before;
        const p = progress_ref.current;
        const key = advanced ? steps[p]?.key : steps[step_index]?.key;
        if (key) {
          scroll_to_step_key(key);
        }
      });
    });
  };
  return {
    scroll_after_advance_click,
    scroll_to_step_key
  };
}